

import React, { useState, useEffect } from 'react';
import { TicketPriority, YesNoUnknown, AppSettings } from '../types'; // Added AppSettings
import { Button } from './Button';
import type { NewTicketFormData } from '../App'; 

interface SelfCheckInPageProps {
  onSaveTicket: (formData: NewTicketFormData) => string; 
  onGoHome: () => void; 
  appSettings: AppSettings; // New prop
}

const initialFormData: Omit<NewTicketFormData, 'priority' | 'amountDue'> & { agreeToTerms: boolean; referralSource: string; } = {
  customer: '',
  customerPhoneNumber: '',
  customerEmail: '',
  deviceModel: '',
  deviceSerialNumber: '',
  subject: '',
  description: '',
  devicePasscode: '',
  dataBackupStatus: YesNoUnknown.UNKNOWN,
  findMyDeviceDisabled: YesNoUnknown.UNKNOWN,
  referralSource: '',
  agreeToTerms: false,
};

// User agreement text will now come from appSettings.intakeAgreementText

const REFERRAL_SOURCES = [
  "", 
  "Google Search",
  "Friend/Family Referral",
  "Social Media (Facebook, Instagram, etc.)",
  "Drove By / Saw Sign",
  "Online Advertisement",
  "Previous Customer",
  "Other",
];

const HomeIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 mr-2" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955a1.125 1.125 0 011.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h7.5" />
  </svg>
);

export const SelfCheckInPage: React.FC<SelfCheckInPageProps> = ({ onSaveTicket, onGoHome, appSettings }) => {
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState<Partial<Record<keyof typeof initialFormData, string>>>({});
  const [submittedTicketId, setSubmittedTicketId] = useState<string | null>(null);

  useEffect(() => {
    let timerId: number | undefined;
    if (submittedTicketId) {
      timerId = window.setTimeout(() => {
        setSubmittedTicketId(null); // Reset to show the form again
      }, 10000); // 10 seconds
    }
    return () => {
      if (timerId) {
        window.clearTimeout(timerId);
      }
    };
  }, [submittedTicketId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
        const { checked } = e.target as HTMLInputElement;
        setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
        setFormData(prev => ({ ...prev, [name]: value }));
    }

    if (errors[name as keyof typeof initialFormData]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof typeof initialFormData, string>> = {};
    if (!formData.customer.trim()) newErrors.customer = 'Your full name is required.';
    if (!formData.deviceModel.trim()) newErrors.deviceModel = 'Device model is required.';
    if (!formData.subject.trim()) newErrors.subject = 'A brief description of the issue is required.';
    if (formData.customerEmail && !/^\S+@\S+\.\S+$/.test(formData.customerEmail)) {
      newErrors.customerEmail = 'Please enter a valid email address.';
    }
    if (!formData.agreeToTerms) newErrors.agreeToTerms = 'You must agree to the terms and conditions.';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      const { agreeToTerms, ...ticketData } = formData; 
      const ticketDataToSave: NewTicketFormData = {
        ...ticketData,
        priority: appSettings.defaultTicketPriority || TicketPriority.MEDIUM, 
        amountDue: 0, 
      };
      const newTicketId = onSaveTicket(ticketDataToSave);
      setSubmittedTicketId(newTicketId);
      setFormData(initialFormData); 
      window.scrollTo(0, 0); 
    }
  };

  const renderError = (field: keyof typeof initialFormData) => {
    return errors[field] ? <p className="text-red-500 text-xs mt-1">{errors[field]}</p> : null;
  };

  if (submittedTicketId) {
    return (
      <div className="min-h-screen bg-[var(--color-primary)] flex flex-col items-center justify-center p-6 text-white">
        <div className="bg-white bg-opacity-20 backdrop-blur-md p-8 md:p-12 rounded-xl shadow-2xl text-center max-w-lg">
           {appSettings.logoBase64 && (
            <img src={appSettings.logoBase64} alt={`${appSettings.companyName} Logo`} className="mx-auto h-16 mb-6 filter brightness-0 invert" />
          )}
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="var(--color-accent)" className="w-20 h-20 mx-auto mb-6">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h1 className="text-3xl font-bold mb-4">Check-in Successful!</h1>
          <p className="text-lg mb-2">Thank you for checking in your device.</p>
          <p className="text-2xl font-semibold mb-6">Your Ticket ID is: <span className="text-[var(--color-accent)]">{submittedTicketId}</span></p>
          <p className="text-md mb-8">Please make a note of this ID and wait for a staff member to assist you shortly.</p>
          <Button variant="secondary" onClick={() => setSubmittedTicketId(null)} className="text-lg px-8 py-3">
            New Check-in
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col items-center justify-center p-4 sm:p-6 relative">
      <Button
        variant="ghost"
        onClick={onGoHome}
        className="absolute top-4 right-4 text-slate-600 hover:text-[var(--color-primary)] hover:bg-[var(--color-primary)] hover:bg-opacity-10 !px-3 !py-2 flex items-center text-sm z-10"
        aria-label="Go to Dashboard"
      >
        <HomeIcon />
        Dashboard
      </Button>

      <div className="bg-white p-6 sm:p-10 rounded-xl shadow-2xl w-full max-w-2xl">
        <div className="text-center mb-8">
          {appSettings.logoBase64 ? (
            <img src={appSettings.logoBase64} alt={`${appSettings.companyName} Logo`} className="mx-auto h-16 mb-4 object-contain" />
          ) : (
            <h1 className="text-3xl sm:text-4xl font-bold text-[var(--color-primary)]">{appSettings.companyName}</h1>
          )}
          <p className="text-lg text-slate-600 mt-1">Customer Self Check-in</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="customer" className="block text-sm font-medium text-slate-700 mb-1">Full Name <span className="text-red-500">*</span></label>
            <input type="text" name="customer" id="customer" value={formData.customer} onChange={handleChange} required
                   className="w-full px-4 py-2.5 border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)] text-base"/>
            {renderError('customer')}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label htmlFor="customerPhoneNumber" className="block text-sm font-medium text-slate-700 mb-1">Phone Number</label>
              <input type="tel" name="customerPhoneNumber" id="customerPhoneNumber" value={formData.customerPhoneNumber} onChange={handleChange}
                     className="w-full px-4 py-2.5 border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)] text-base"/>
            </div>
            <div>
              <label htmlFor="customerEmail" className="block text-sm font-medium text-slate-700 mb-1">Email Address</label>
              <input type="email" name="customerEmail" id="customerEmail" value={formData.customerEmail} onChange={handleChange}
                     className="w-full px-4 py-2.5 border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)] text-base"/>
              {renderError('customerEmail')}
            </div>
          </div>
          
          <div>
            <label htmlFor="referralSource" className="block text-sm font-medium text-slate-700 mb-1">How did you hear about us? (Optional)</label>
            <select name="referralSource" id="referralSource" value={formData.referralSource} onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-slate-300 bg-white rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)] text-base">
              {REFERRAL_SOURCES.map(source => (
                <option key={source} value={source}>{source === "" ? "Select an option..." : source}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label htmlFor="deviceModel" className="block text-sm font-medium text-slate-700 mb-1">Device Model (e.g., iPhone 13, MacBook Pro) <span className="text-red-500">*</span></label>
              <input type="text" name="deviceModel" id="deviceModel" value={formData.deviceModel} onChange={handleChange} required
                     className="w-full px-4 py-2.5 border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)] text-base"/>
              {renderError('deviceModel')}
            </div>
            <div>
              <label htmlFor="deviceSerialNumber" className="block text-sm font-medium text-slate-700 mb-1">Device Serial Number (Optional)</label>
              <input type="text" name="deviceSerialNumber" id="deviceSerialNumber" value={formData.deviceSerialNumber} onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)] text-base"/>
            </div>
          </div>

          <div>
            <label htmlFor="subject" className="block text-sm font-medium text-slate-700 mb-1">Brief Description of Issue <span className="text-red-500">*</span></label>
            <input type="text" name="subject" id="subject" value={formData.subject} onChange={handleChange} required
                   placeholder="e.g., Cracked screen, Won't turn on, Battery drains fast"
                   className="w-full px-4 py-2.5 border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)] text-base"/>
            {renderError('subject')}
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-slate-700 mb-1">Additional Details (Optional)</label>
            <textarea name="description" id="description" value={formData.description} onChange={handleChange} rows={3}
                      placeholder="Please provide any other relevant information about the issue."
                      className="w-full px-4 py-2.5 border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)] text-base"></textarea>
          </div>
          
          <div>
            <label htmlFor="devicePasscode" className="block text-sm font-medium text-slate-700 mb-1">Device Passcode (Optional)</label>
            <input type="text" name="devicePasscode" id="devicePasscode" value={formData.devicePasscode} onChange={handleChange}
                   className="w-full px-4 py-2.5 border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)] text-base"/>
            <p className="text-xs text-slate-500 mt-1">Providing your passcode helps us diagnose and repair your device faster. It will be kept confidential.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label htmlFor="dataBackupStatus" className="block text-sm font-medium text-slate-700 mb-1">Is your data backed up?</label>
              <select name="dataBackupStatus" id="dataBackupStatus" value={formData.dataBackupStatus} onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-slate-300 bg-white rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)] text-base">
                {Object.values(YesNoUnknown).map(s => <option key={s} value={s}>{s === YesNoUnknown.UNKNOWN ? 'Select...' : s}</option>)}
              </select>
            </div>
             <div>
              <label htmlFor="findMyDeviceDisabled" className="block text-sm font-medium text-slate-700 mb-1">Is "Find My Device" disabled?</label>
              <select name="findMyDeviceDisabled" id="findMyDeviceDisabled" value={formData.findMyDeviceDisabled} onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-slate-300 bg-white rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)] text-base">
                {Object.values(YesNoUnknown).map(s => <option key={s} value={s}>{s === YesNoUnknown.UNKNOWN ? 'Select...' : s}</option>)}
              </select>
              <p className="text-xs text-slate-500 mt-1">Required for most Apple device repairs.</p>
            </div>
          </div>

          <div className="space-y-3 pt-3">
            <h3 className="text-md font-semibold text-slate-700">Terms and Conditions</h3>
            <div className="h-24 overflow-y-auto p-3 border border-slate-200 rounded-md bg-slate-50 text-xs text-slate-600">
              {appSettings.intakeAgreementText}
            </div>
            <div className="flex items-start">
              <input type="checkbox" name="agreeToTerms" id="agreeToTerms" checked={formData.agreeToTerms} onChange={handleChange} required
                     className="h-5 w-5 text-[var(--color-primary)] border-slate-300 rounded focus:ring-[var(--color-primary)] mt-0.5" />
              <label htmlFor="agreeToTerms" className="ml-2.5 block text-sm text-slate-700">
                I have read and agree to the terms and conditions outlined above. <span className="text-red-500">*</span>
              </label>
            </div>
            {renderError('agreeToTerms')}
          </div>

          <div className="pt-5">
            <Button type="submit" variant="primary" className="w-full text-lg py-3.5">
              Submit Check-in
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
